import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import api from '@/lib/api';
import type { TodoItemResponse } from '@/lib/api';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { useCompletedTimeline, useTodoSections } from '../hooks';
import { usePrincipalDisplayNames } from '../hooks/usePrincipalDisplayNames';
import { CompletedTimeline } from '../components';

export function CompletedTimelinePage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedHolderId, isLoading: switcherLoading } = useHolderSwitcher();

  const { sections } = useTodoSections(selectedHolderId, listId!);
  const { items, loading, error } = useCompletedTimeline({
    holderId: selectedHolderId,
    listId: listId!,
  });
  const { resolve: resolveCompletedBy } = usePrincipalDisplayNames();

  const handleUncomplete = async (item: TodoItemResponse) => {
    if (!selectedHolderId) return;
    const sectionExists = item.sectionId && sections.some((s) => s.sectionId === item.sectionId);
    await api.updateTodoItem(selectedHolderId, item.itemId, {
      status: 'pending',
      ...(item.sectionId && !sectionExists ? { sectionId: null } : {}),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.todoLists.detail(selectedHolderId, listId!),
    });
  };

  if (switcherLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (!selectedHolderId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-sm text-muted-foreground">Select a workspace to view completed items.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/a/arche_todo_lists/${listId}`)}
          aria-label="Back to list"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Completed Items</h1>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No completed items yet</p>
        </div>
      ) : (
        <CompletedTimeline
          items={items as TodoItemResponse[]}
          sections={sections}
          loading={loading}
          showCompletedBy
          resolveCompletedBy={resolveCompletedBy}
          onUncomplete={handleUncomplete}
        />
      )}
    </div>
  );
}
